<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  bundles: {
    type: Array,
    default: () => [],
  },
  attributes: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const attributeLabelByKey = computed(() => {
  return props.attributes.reduce((acc, attribute) => {
    acc[attribute.attribute_key] =
      attribute.attribute_display_name || attribute.attribute_key;
    return acc;
  }, {});
});

const resolveFieldLabel = field => {
  return (
    attributeLabelByKey.value[field.attribute_key] || field.attribute_key || '—'
  );
};

const resolveFieldOrder = field => field.order || 0;

const renderedBundles = computed(() => {
  return [...props.bundles]
    .map(bundle => ({
      ...bundle,
      fields: [...(bundle.fields || [])]
        .map(field => ({
          ...field,
          label: resolveFieldLabel(field),
        }))
        .sort((a, b) => resolveFieldOrder(a) - resolveFieldOrder(b)),
    }))
    .sort((a, b) => (a.position || 0) - (b.position || 0));
});
</script>

<template>
  <section
    class="flex flex-col overflow-hidden border rounded-xl border-n-weak bg-n-solid-2"
  >
    <header class="flex items-center justify-between gap-2 px-4 h-11 border-b border-n-weak">
      <span class="flex items-center gap-1.5 min-w-0 text-sm font-medium text-n-slate-12">
        <span class="i-lucide-package size-4 shrink-0 text-n-slate-10" />
        <span class="truncate">{{ t('ATTRIBUTES_MGMT.BUNDLES.TITLE') }}</span>
      </span>
      <span
        class="px-1.5 py-0.5 text-xs rounded-md tabular-nums bg-n-alpha-2 text-n-slate-11"
      >
        {{ renderedBundles.length }}
      </span>
    </header>

    <div class="flex flex-col gap-3 p-4">
      <p class="text-xs leading-5 text-n-slate-10">
        {{ t('ATTRIBUTES_MGMT.BUNDLES.DESCRIPTION') }}
      </p>

      <p v-if="isLoading" class="text-sm text-n-slate-10">
        {{ t('ATTRIBUTES_MGMT.BUNDLES.LOADING') }}
      </p>

      <p v-else-if="!renderedBundles.length" class="text-sm text-n-slate-10">
        {{ t('ATTRIBUTES_MGMT.BUNDLES.EMPTY') }}
      </p>

      <div v-else class="flex flex-col gap-3">
        <article
          v-for="bundle in renderedBundles"
          :key="bundle.id"
          class="flex flex-col gap-2 rounded-lg border border-n-weak bg-n-background p-3"
        >
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{ bundle.bundle_name }}
              </div>
              <div class="truncate font-mono text-xs text-n-slate-10">
                {{ bundle.bundle_key }}
              </div>
            </div>
            <span
              v-if="bundle.active"
              class="inline-flex shrink-0 items-center rounded-md bg-n-teal-3 px-1.5 py-0.5 text-xs text-n-teal-11"
            >
              {{ t('ATTRIBUTES_MGMT.BUNDLES.ACTIVE') }}
            </span>
          </div>

          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="field in bundle.fields.slice(0, 4)"
              :key="field.attribute_key"
              class="inline-flex max-w-full items-center rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
            >
              <span class="truncate">{{ field.label }}</span>
            </span>
            <span
              v-if="bundle.fields.length > 4"
              class="inline-flex items-center rounded-md bg-n-alpha-2 px-1.5 py-0.5 text-xs text-n-slate-11"
            >
              +{{ bundle.fields.length - 4 }}
            </span>
          </div>

          <div class="text-xs text-n-slate-10">
            {{ t('ATTRIBUTES_MGMT.BUNDLES.FIELDS', { count: bundle.fields.length }) }}
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
